import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaSpinner, FaClipboardCheck, FaTools } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { jobCardService, bookingService } from '../../services/apiService';

const STATUS_LABELS = {
  Created: 'Awaiting Estimate',
  PendingCustomerApproval: 'Approval Required',
  CustomerApproved: 'Approved',
  CustomerRejected: 'Rejected',
  InProgress: 'In Progress',
  ReadyForQC: 'QC Stage',
  QCRejected: 'QC — garage updating',
  QCApproved: 'QC Approved',
  Billed: 'Invoice Ready',
  Paid: 'Paid',
  Delivered: 'Delivered',
  Closed: 'Closed',
  Cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  Created: 'bg-slate-100 text-slate-800',
  PendingCustomerApproval: 'bg-amber-100 text-amber-800',
  CustomerApproved: 'bg-emerald-100 text-emerald-800',
  CustomerRejected: 'bg-red-100 text-red-800',
  InProgress: 'bg-blue-100 text-blue-800',
  ReadyForQC: 'bg-teal-100 text-teal-800',
  QCRejected: 'bg-orange-100 text-orange-900',
  QCApproved: 'bg-indigo-100 text-indigo-800',
  Billed: 'bg-purple-100 text-purple-800',
  Paid: 'bg-green-100 text-green-800',
  Delivered: 'bg-green-100 text-green-800',
  Closed: 'bg-gray-200 text-gray-800',
  Cancelled: 'bg-gray-100 text-gray-600',
};

function bookingRowToJob(row) {
  return {
    _id: row.jobCardId,
    jobCardId: row.jobCardNumber,
    status: row.jobStatus || 'Created',
    vehicle: row.vehicle,
    garageId: {
      garageDetails: { garageName: row.garage?.displayName },
      name: row.garage?.displayName,
    },
    serviceType: row.serviceType,
    slotDate: row.slotDate,
    slotTime: row.slotTime,
    bookingId: row.bookingId,
  };
}

function mergeJobs(jobCards, bookings) {
  const byId = new Map();

  (jobCards || []).forEach((job) => {
    const id = String(job._id || job.id || '');
    if (id) byId.set(id, job);
  });

  (bookings || []).forEach((row) => {
    const mapped = bookingRowToJob(row);
    const id = String(mapped._id || '');
    if (!id) return;
    if (byId.has(id)) {
      byId.set(id, { ...mapped, ...byId.get(id), bookingId: row.bookingId });
    } else {
      byId.set(id, mapped);
    }
  });

  return Array.from(byId.values()).sort((a, b) => {
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return tb - ta;
  });
}

const MyServiceJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchJobs = async () => {
    setLoading(true);
    setLoadError('');

    let jobCards = [];
    let bookings = [];
    let jobCardsError = null;
    let bookingsError = null;

    try {
      const data = await jobCardService.getMy();
      jobCards = Array.isArray(data) ? data : (data?.jobCards ?? data?.jobs ?? []);
    } catch (e) {
      jobCardsError = e;
    }

    try {
      const data = await bookingService.listMy();
      bookings = Array.isArray(data) ? data : [];
    } catch (e) {
      bookingsError = e;
    }

    const merged = mergeJobs(jobCards, bookings);
    setJobs(merged);

    if (!merged.length && jobCardsError && bookingsError) {
      const msg = jobCardsError.message || bookingsError.message || 'Could not load your service jobs';
      setLoadError(msg);
      toast.error(msg);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const openBookingStatus = async (e, job) => {
    e.stopPropagation();
    const jobId = job._id || job.id;
    if (job.bookingId) {
      navigate(`/service/booking/status/${job.bookingId}`);
      return;
    }
    try {
      const data = await bookingService.getStatusByJobCard(jobId);
      if (data?.bookingId) {
        navigate(`/service/booking/status/${data.bookingId}`);
      } else {
        navigate(`/service/job/${jobId}`);
      }
    } catch {
      navigate(`/service/job/${jobId}`);
    }
  };

  const pendingCount = jobs.filter((j) => j.status === 'PendingCustomerApproval').length;

  return (
    <div className="checkout-light-page min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-6 sm:py-8 px-3 sm:px-4 pb-28">
      <div className="section-container max-w-4xl mx-auto w-full">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-red-600 hover:text-red-700 font-medium mb-4 inline-flex items-center gap-1 text-sm"
          >
            ← Back
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">My Service Jobs</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            View your service requests and approve estimates from the garage.
          </p>
          {pendingCount > 0 && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <FaClipboardCheck className="text-amber-600 shrink-0" />
              <span className="text-amber-800 font-medium text-sm">
                {pendingCount} estimate{pendingCount > 1 ? 's' : ''} awaiting your approval
              </span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="py-12 text-center text-gray-500 flex items-center justify-center gap-2">
              <FaSpinner className="animate-spin" /> Loading…
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 sm:p-12 text-center">
              <FaTools className="mx-auto text-4xl text-gray-300 mb-4" />
              <p className="text-gray-600 mb-2">You have no service jobs yet.</p>
              {loadError && (
                <p className="text-sm text-red-600 mb-4">{loadError}</p>
              )}
              <button
                type="button"
                onClick={() => navigate('/service/booking')}
                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700"
              >
                Book a Service
              </button>
            </div>
          ) : (
            jobs.map((job) => (
              <div
                key={job._id || job.id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all hover:shadow-md ${
                  job.status === 'PendingCustomerApproval' ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-100'
                }`}
              >
                <div
                  className="p-4 sm:p-6 cursor-pointer"
                  onClick={() => navigate(`/service/job/${job._id || job.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/service/job/${job._id || job.id}`);
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {(() => {
                        const qcApprovedBySuperadmin =
                          job?.qc?.approvedByRole === 'superadmin' ||
                          (!job?.qc?.approvedByRole &&
                            (job?.timeline || []).some((t) => t?.status === 'QCApproved' && t?.byRole === 'superadmin'));
                        const effectiveStatus =
                          job.status === 'QCApproved'
                            ? qcApprovedBySuperadmin
                              ? 'Billed'
                              : 'InProgress'
                            : job.status === 'ReadyForQC' || job.status === 'QCRejected'
                              ? 'InProgress'
                              : job.status;
                        return (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-gray-900">
                              Job #{(job.jobCardId || job._id || '').toString().slice(-8)}
                            </span>
                            <span
                              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                STATUS_COLORS[effectiveStatus] || 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {STATUS_LABELS[effectiveStatus] || effectiveStatus}
                            </span>
                            {job.status === 'PendingCustomerApproval' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                                <FaClipboardCheck /> Review & Approve
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      <p className="text-sm text-gray-600 mt-1 break-words">
                        {job.vehicle?.registrationNumber || ''} {job.vehicle?.make || ''} {job.vehicle?.model || ''}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Garage: {job.garageId?.garageDetails?.garageName || job.garageId?.name || '—'}
                        {job.serviceType ? ` · ${job.serviceType}` : ''}
                      </p>
                      {job.status === 'PendingCustomerApproval' && job.estimate?.total != null && (
                        <p className="text-sm font-semibold text-gray-500 mt-1">
                          Estimate: ₹{Number(job.estimate.total).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => openBookingStatus(e, job)}
                        className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Track
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/service/job/${job._id || job.id}`);
                        }}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 text-sm"
                      >
                        <FaEye /> View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-8">
          <button
            type="button"
            onClick={() => navigate('/service/booking')}
            className="w-full sm:w-auto px-6 py-3 border-2 border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors text-sm sm:text-base"
          >
            Book New Service
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyServiceJobs;
